import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast, Toaster } from 'sonner';
import { Plus, Edit2, Trash2, X, Save, GripVertical, Building, Heart, Target, DollarSign, Award, Briefcase } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const iconOptions = [
  { value: 'Building', label: 'Building', icon: Building },
  { value: 'Heart', label: 'Heart', icon: Heart },
  { value: 'Target', label: 'Target', icon: Target },
  { value: 'DollarSign', label: 'Dollar', icon: DollarSign },
  { value: 'Award', label: 'Award', icon: Award },
  { value: 'Briefcase', label: 'Briefcase', icon: Briefcase },
];

const colorOptions = [
  { value: 'tech-blue', label: 'Blue' },
  { value: 'care-teal', label: 'Teal' },
  { value: 'heritage-orange', label: 'Orange' },
  { value: 'purple-500', label: 'Purple' },
  { value: 'pink-500', label: 'Pink' },
  { value: 'indigo-500', label: 'Indigo' },
];

const AdminSolutionsPage = () => {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    features: [],
    icon: 'Building',
    image_url: '',
    color: 'tech-blue',
    order: 0
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => { loadSolutions(); }, []);

  const loadSolutions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/cms/solutions?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSolutions(data);
    } catch (e) {
      toast.error('Failed to load solutions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingId 
        ? `${API_URL}/api/cms/solutions/${editingId}`
        : `${API_URL}/api/cms/solutions`;
      
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success(editingId ? 'Solution updated' : 'Solution created');
      resetForm();
      loadSolutions();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this solution?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/cms/solutions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Solution deleted');
      loadSolutions();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (solution) => {
    setFormData({
      title: solution.title,
      description: solution.description,
      features: solution.features || [],
      icon: solution.icon || 'Building',
      image_url: solution.image_url || '',
      color: solution.color || 'tech-blue',
      order: solution.order || 0
    });
    setEditingId(solution.id);
    setShowForm(true);
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({ ...prev, features: [...prev.features, newFeature.trim()] }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', features: [], icon: 'Building', image_url: '', color: 'tech-blue', order: 0 });
    setEditingId(null);
    setShowForm(false);
  };

  const IconComponent = iconOptions.find(i => i.value === formData.icon)?.icon || Building;

  if (loading) return <div className="text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Solutions</h1>
          <p className="text-text-secondary mt-1">Manage NDIS provider solution types</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          Add Solution
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl border-border-line my-8">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle>{editingId ? 'Edit' : 'Add'} Solution</CardTitle>
              <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                <X size={20} />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    placeholder="e.g., NDIS Registered Providers"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Description *</label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                    placeholder="Describe this solution type..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Icon</label>
                    <select
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    >
                      {iconOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Color</label>
                    <select
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    >
                      {colorOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Image URL</label>
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    placeholder="https://example.com/image.jpg"
                  />
                  {formData.image_url && (
                    <img src={formData.image_url} alt="Preview" className="mt-2 h-24 w-full object-cover rounded-lg" />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Features</label>
                  <div className="flex space-x-2 mb-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      className="flex-1 px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      placeholder="Add a feature..."
                    />
                    <Button type="button" onClick={addFeature} variant="outline">
                      <Plus size={16} />
                    </Button>
                  </div>
                  <ul className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <li key={index} className="flex items-center justify-between bg-page-bg px-3 py-2 rounded-lg">
                        <span className="text-sm text-text-primary">{feature}</span>
                        <button type="button" onClick={() => removeFeature(index)} className="text-red-500 hover:text-red-600">
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-24 px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                    <Save size={18} className="mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Solutions List */}
      <div className="grid md:grid-cols-2 gap-6">
        {solutions.map((solution) => {
          const Icon = iconOptions.find(i => i.value === solution.icon)?.icon || Building;
          return (
            <Card key={solution.id} className="border-border-line">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-xl bg-${solution.color}/10 flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`text-${solution.color}`} size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-text-primary truncate">{solution.title}</h3>
                      <span className="text-xs bg-tech-blue/10 text-tech-blue px-2 py-1 rounded">#{solution.order}</span>
                    </div>
                    <p className="text-sm text-text-secondary line-clamp-2 mb-3">{solution.description}</p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(solution.features || []).slice(0, 3).map((f, i) => (
                        <span key={i} className="text-xs bg-page-bg text-text-secondary px-2 py-1 rounded">{f}</span>
                      ))}
                      {(solution.features || []).length > 3 && (
                        <span className="text-xs text-text-secondary">+{solution.features.length - 3} more</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(solution)}>
                        <Edit2 size={14} className="mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(solution.id)} className="text-red-500 hover:text-red-600">
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {solutions.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          <Building className="mx-auto mb-4" size={48} />
          <p>No solutions yet. Add your first solution type.</p>
        </div>
      )}
    </div>
  );
};

export default AdminSolutionsPage;
